import { useState, useEffect, useCallback } from "react";
import apiClient from "../../auth/apiClient";
import CreditCardOnFileService from "../../services/CreditCardOnFileService";
import { useEnqueueSnackbar } from "../useEnqueueSnackbar";
import useHubContext from "../useHubContext";
import { CARD_ON_FILE_STATE_CHANGE } from "../../constants";

const creditCardOnFileService = new CreditCardOnFileService(apiClient);

export const useCardOnFile = (contractID) => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const { portalHub, Connected: PortalHubConnected } = useHubContext();

  const [loading, setLoading] = useState(true);
  const [cardsOnFile, setCardsOnFile] = useState(undefined);
  const [transactionState, setTransactionState] = useState(undefined);
  const [hasCardOnFile, setHasCardOnFile] = useState(false);

  function padTo2Digits(num) {
    return num.toString().padStart(2, "0");
  }

  const fetchCardOnFile = useCallback(async () => {
    if (!contractID) {
      setLoading(false);
      return;
    }

    setLoading(true);

    let response;
    let isCardOnFileAvailable = false;
    let contractTransactionState;

    try {
      response = await creditCardOnFileService.getCreditCardOnFile(contractID);
    } catch (ex) {
      if (ex.response?.status !== 404) {
        enqueueSnackbar("Failed to get card on file", {
          variant: "error",
          tag: "getCardOnFile",
        });
      }
    }

    try {
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        response.data.forEach((card) => {
          var expDate = new Date(card.expirationDate);
          var month = padTo2Digits(expDate.getMonth() + 1);
          var year = expDate.getFullYear();
          card.expirationDate = month + "/" + year;
          card.maskedCardNumber = "**** **** **** " + card.last4;
          card.cardType = getCardByCardType(card.creditCardDescription);
        });
        isCardOnFileAvailable = true;
      }

      if (isCardOnFileAvailable) {
        try {
          const transactionStateResponse = await creditCardOnFileService.getTransactionState(contractID);
          contractTransactionState = transactionStateResponse?.data;
        } catch (error) {
          console.error("Error fetching transaction state");
        }
      }

      setCardsOnFile(isCardOnFileAvailable ? response.data : null);
      setTransactionState(contractTransactionState);
      setHasCardOnFile(isCardOnFileAvailable);
      setLoading(false);
    } catch (ex) {
      console.log("Error processing card on file with its transaction state");
      setLoading(false);
    }
  }, [contractID]);

  function getCardByCardType(cardType) {
    let imagePath;
    switch (cardType?.toLowerCase()) {
      case "visa":
        imagePath = require("../../assets/creditcards/visa.png");
        break;
      case "mastercard":
        imagePath = require("../../assets/creditcards/mastercard.png");
        break;
      case "discover":
        imagePath = require("../../assets/creditcards/discover.png");
        break;
      case "american express":
        imagePath = require("../../assets/creditcards/americanexpress.png");
        break;
      default:
        imagePath = require("../../assets/creditcards/other.png");
        break;
    }
    return {
      imageSrc: imagePath,
      name: cardType
    };
  }

  useEffect(() => {
    if (!PortalHubConnected || !contractID) return;

    portalHub.subscribe(CARD_ON_FILE_STATE_CHANGE, (data) => {
      console.log("Received hub topic:CARD_ON_FILE_STATE_CHANGE with details", data);
      var dataJson = JSON.parse(data);
      var message = JSON.parse(dataJson.Message);
      if (message.ContractID === contractID) {
        console.log("Card On File Transaction State changed to ", message.TransactionState);
        fetchCardOnFile();
      }
    });

    return () => {
      portalHub.unsubscribe(CARD_ON_FILE_STATE_CHANGE);
    };
  }, [PortalHubConnected, contractID, fetchCardOnFile]);

  useEffect(() => {
    fetchCardOnFile();
  }, [fetchCardOnFile]);

  const setActiveCard = useCallback(async (recurringCustomerCardID) => {
    try {
      await creditCardOnFileService.setActiveCardOnFile(contractID, recurringCustomerCardID);
      await fetchCardOnFile();
    } catch (err) {
      enqueueSnackbar("Failed to set active card", {
        variant: "error",
        tag: "setActiveCardError",
      });
    }
  }, [contractID, fetchCardOnFile]);

  return {
    loading,
    cardsOnFile,
    transactionState,
    hasCardOnFile,
    refetch: fetchCardOnFile,
    setActiveCard,
  };
};

export default useCardOnFile;
